import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import AnimateOnScroll, { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';
import { TeacherGridCardSkeleton, CourseGridCardSkeleton } from '@/components/common/LoadingSkeleton';
import { SEO, createBreadcrumbSchema, createFAQSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useTeachers } from '@/hooks/use-public-api';
import { useCourses } from '@/hooks/use-course-api';
import { specialtyLabel } from '@/lib/specialties';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import {
  ArrowRight,
  Star,
  BadgeCheck,
  UserRound,
  KeyRound,
  BookOpen,
  LineChart,
  CalendarClock,
  ShieldCheck,
  Lock,
  Video,
  Repeat,
  UserPlus,
  Baby,
  Search,
  CalendarCheck,
  Sparkles,
} from 'lucide-react';

/**
 * /quran-for-kids — landing page for the children's campaign.
 *
 * Ad traffic lands here rather than on /teachers, so everything a parent needs
 * to decide is on one page: who teaches, how a child starts at their age, what
 * we do because they are minors, and the four steps to a first lesson.
 *
 * The teacher grid is the real listing filtered to `specialty=Child`, which
 * LIKE-matches the three children's tags in use ("Children", "Children's
 * Quran", "Children's Education"). That keeps `kids.why.1.desc`'s claim — every
 * teacher shown lists children's teaching as a specialty — literally true, and
 * it means the page has no separate roster to keep in sync.
 */

/**
 * Honorifics that open almost every teacher name here ("Ustadha Fatima
 * Hassan", "الأستاذة فاطمة حسن"). Left in, the initials collapse to U/U/U
 * across the whole grid and stop distinguishing anything.
 */
const HONORIFICS = new Set([
  'ustadh', 'ustadha', 'ustaadh', 'ustaadha', 'sheikh', 'shaykh', 'sheikha',
  'shaykha', 'qari', 'qariah', 'qariah', 'hafiz', 'hafiza', 'dr', 'dr.',
  'imam', 'mufti',
  'الأستاذ', 'الأستاذة', 'أستاذ', 'أستاذة', 'الشيخ', 'الشيخة', 'شيخ', 'شيخة',
  'القارئ', 'القارئة', 'قارئ', 'قارئة', 'الحافظ', 'الحافظة', 'د.', 'الدكتور',
  'الدكتورة',
]);

/**
 * Initials for the avatar fallback.
 *
 * Only 1 of the 35 approved teachers has uploaded a photo, so on this page the
 * fallback is the common case, not the exception — a grid of six identical grey
 * silhouettes is what a parent arriving from an ad would actually see. Initials
 * at least distinguish the cards from one another.
 */
function initialsOf(name: string): string {
  const words = name
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => !HONORIFICS.has(word.toLowerCase()));

  return (words.length ? words : name.split(/\s+/).filter(Boolean))
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? '')
    .join('');
}

const KIDS_SPECIALTY = 'Child';

// One request feeds both the grid (top 6 by rating) and the stats strip (count
// + mean across every matching teacher). Fetching only 6 would make the average
// the average of the six best, which is not what the label claims.
const KIDS_TEACHER_FETCH_LIMIT = 24;
const KIDS_TEACHER_SHOWN = 6;

interface KidsTeacher {
  name: string;
  slug?: string;
  full_name: string;
  full_name_ar?: string;
  profile_image?: string;
  specialties?: string;
  specialties_list?: string[];
  hourly_rate: number;
  trial_session_price?: number;
  currency?: string;
  ijazah_verified?: number;
  average_rating?: number;
  total_reviews?: number;
  years_experience?: number;
}

interface KidsCourse {
  name: string;
  slug: string;
  title: string;
  title_ar?: string;
  thumbnail?: string;
  level?: string;
  total_lessons?: number;
  is_free?: boolean;
  effective_price?: number;
  price?: number;
}

const WHY = [
  { key: '1', icon: Baby },
  { key: '2', icon: UserRound },
  { key: '3', icon: KeyRound },
  { key: '4', icon: BookOpen },
  { key: '5', icon: LineChart },
  { key: '6', icon: CalendarClock },
] as const;

const PATHS = ['early', 'middle', 'teen'] as const;

const HOW = [
  { key: '1', icon: UserPlus },
  { key: '2', icon: Baby },
  { key: '3', icon: Search },
  { key: '4', icon: CalendarCheck },
] as const;

const SAFETY = [
  { key: '1', icon: BadgeCheck },
  { key: '2', icon: Lock },
  { key: '3', icon: Video },
  { key: '4', icon: Repeat },
] as const;

const FAQ_KEYS = ['1', '2', '3', '4', '5', '6', '7'] as const;

const TeacherCard = ({ teacher }: { teacher: KidsTeacher }) => {
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();

  const name = isRTL && teacher.full_name_ar ? teacher.full_name_ar : teacher.full_name;
  const specialties =
    teacher.specialties_list ||
    (teacher.specialties ? teacher.specialties.split(',').map((s) => s.trim()).filter(Boolean) : []);
  const trial = teacher.trial_session_price;

  return (
    <Link
      to={`/teachers/${encodeURIComponent(teacher.slug || teacher.name)}`}
      className="h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 h-full flex flex-col"
      >
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          {teacher.profile_image ? (
            <img
              src={teacher.profile_image}
              alt={name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-600 to-emerald-800"
              aria-hidden="true"
            >
              <span className="text-3xl font-semibold text-amber-300/90 tracking-wide">
                {initialsOf(name)}
              </span>
            </div>
          )}
        </div>

        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-foreground leading-tight">{name}</h3>
            {teacher.average_rating ? (
              <span className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
                <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />
                {teacher.average_rating.toFixed(2)}
              </span>
            ) : null}
          </div>

          {teacher.ijazah_verified ? (
            <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 mb-3">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {t('kids.teachers.ijazahVerified')}
            </p>
          ) : (
            <div className="mb-3" />
          )}

          {specialties.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 mb-4">
              {specialties.slice(0, 3).map((s) => (
                <li
                  key={s}
                  className="text-xs bg-muted text-muted-foreground rounded-full px-2.5 py-1"
                >
                  {specialtyLabel(s, isRTL)}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-auto pt-3 border-t border-border flex items-baseline justify-between gap-2">
            <span className="text-foreground font-semibold">
              {formatPrice(teacher.hourly_rate)}
              <span className="text-xs text-muted-foreground font-normal">
                {t('kids.teachers.perHour')}
              </span>
            </span>
            {trial === 0 ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                {t('kids.teachers.trialFree')}
              </span>
            ) : trial ? (
              <span className="text-xs text-muted-foreground">
                {t('kids.teachers.trialFrom')} {formatPrice(trial)}
              </span>
            ) : null}
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

const CourseCard = ({ course }: { course: KidsCourse }) => {
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();

  const title = isRTL && course.title_ar ? course.title_ar : course.title;
  const price = course.effective_price ?? course.price ?? 0;

  return (
    <Link
      to={`/courses/${encodeURIComponent(course.slug || course.name)}`}
      className="h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 h-full flex flex-col"
      >
        <div className="aspect-video bg-muted overflow-hidden">
          {course.thumbnail ? (
            <img
              src={course.thumbnail}
              alt={title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-700 to-emerald-900"
              aria-hidden="true"
            >
              <BookOpen className="h-9 w-9 text-amber-300/80" />
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-semibold text-foreground leading-snug mb-2">{title}</h3>
          {course.total_lessons ? (
            <p className="text-sm text-muted-foreground">
              {course.total_lessons} {t('kids.courses.lessons')}
            </p>
          ) : null}
          <span className="mt-auto pt-3 text-foreground font-semibold">
            {course.is_free ? t('kids.courses.free') : formatPrice(price)}
          </span>
        </div>
      </motion.div>
    </Link>
  );
};

const QuranForKids = () => {
  const { t, isRTL, language } = useLanguage();
  const prefersReducedMotion = useReducedMotion();

  const {
    data: teacherData,
    isLoading: teachersLoading,
    isError: teachersError,
  } = useTeachers({
    specialty: KIDS_SPECIALTY,
    limit: KIDS_TEACHER_FETCH_LIMIT,
    sort_by: 'average_rating',
    sort_order: 'desc',
  });

  // No sort_by: the courses endpoint interpolates it straight into ORDER BY
  // (unlike the teacher listing, which allow-lists it), so the default is the
  // only value worth sending from a public page.
  const { data: courseData, isLoading: coursesLoading } = useCourses({
    level: 'Beginner',
    limit: 3,
  });

  // `TeacherListItem` in types/api.ts does not match what get_teachers actually
  // returns (it declares specialties as string[]; the endpoint sends the raw
  // comma-separated string plus specialties_list), so the cast goes via unknown.
  const teachers = (teacherData?.teachers as unknown as KidsTeacher[] | undefined) ?? [];
  const courses = (courseData?.courses as unknown as KidsCourse[] | undefined) ?? [];

  const teacherCount = teacherData?.total ?? teachers.length;
  const averageRating = useMemo(() => {
    const rated = teachers.filter((x) => (x.average_rating ?? 0) > 0);
    if (!rated.length) return null;
    return (rated.reduce((sum, x) => sum + (x.average_rating ?? 0), 0) / rated.length).toFixed(2);
  }, [teachers]);

  const structuredData = useMemo(() => {
    const inLanguage = isRTL ? 'ar' : 'en';
    return {
      '@context': 'https://schema.org',
      '@graph': [
        createFAQSchema(
          FAQ_KEYS.map((k) => ({ question: t(`kids.faq.q${k}`), answer: t(`kids.faq.a${k}`) })),
          inLanguage
        ),
        createBreadcrumbSchema(
          [
            { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
            { name: t('kids.breadcrumb'), url: 'https://hudanlilmuttaqeen.com/quran-for-kids' },
          ],
          inLanguage
        ),
      ],
    };
    // `language` is what actually flips t(); isRTL is derived from it.
  }, [t, isRTL, language]);

  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <Layout>
      <SEO
        titleKey="seo.kids.title"
        descriptionKey="seo.kids.description"
        keywordsKey="seo.kids.keywords"
        url="https://hudanlilmuttaqeen.com/quran-for-kids"
        structuredData={structuredData}
      />

      {/* ---------------------------------------------------------------- Hero */}
      <section
        className="relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark"
        dir={dir}
      >
        <div className="absolute inset-0 islamic-pattern-overlay opacity-60" />
        <div className="absolute top-10 end-[10%] w-[500px] h-[500px] rounded-full bg-amber-400/8 blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 start-[5%] w-[400px] h-[400px] rounded-full bg-emerald-300/8 blur-[80px] pointer-events-none" />

        <div className="container relative z-10 py-16 md:py-24">
          <motion.div
            initial={prefersReducedMotion ? undefined : { opacity: 0, y: 24 }}
            animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-amber-400" aria-hidden="true" />
              <span className="text-emerald-100 text-sm font-medium">{t('kids.hero.badge')}</span>
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-[1.15]">
              {t('kids.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-emerald-100/80 mb-8 leading-relaxed">
              {t('kids.hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Button
                asChild
                size="lg"
                className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-7 h-12 text-base shadow-glow-gold"
              >
                <Link to="/register/parent" className="flex items-center gap-2">
                  {t('kids.hero.ctaPrimary')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-xl px-7 h-12 text-base"
              >
                <Link to="/teachers">{t('kids.hero.ctaSecondary')}</Link>
              </Button>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {(['trust1', 'trust2', 'trust3', 'trust4'] as const).map((k) => (
                <li key={k} className="flex items-center gap-2 text-emerald-100/90 text-sm">
                  <BadgeCheck className="h-4 w-4 text-amber-400 shrink-0" aria-hidden="true" />
                  {t(`kids.hero.${k}`)}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* --------------------------------------------------------------- Stats */}
      <section className="border-b border-border bg-card" dir={dir}>
        <div className="container py-8">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">
                {teachersLoading ? '—' : teacherCount}
              </dd>
              <dt className="text-xs text-muted-foreground mt-1">{t('kids.stats.teachersLabel')}</dt>
            </div>
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">
                {t('kids.stats.agesValue')}
              </dd>
              <dt className="text-xs text-muted-foreground mt-1">{t('kids.stats.agesLabel')}</dt>
            </div>
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">
                {averageRating ?? '—'}
              </dd>
              <dt className="text-xs text-muted-foreground mt-1">{t('kids.stats.ratingLabel')}</dt>
            </div>
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">
                {t('kids.stats.formatValue')}
              </dd>
              <dt className="text-xs text-muted-foreground mt-1">{t('kids.stats.formatLabel')}</dt>
            </div>
          </dl>
        </div>
      </section>

      {/* ----------------------------------------------------------------- Why */}
      <section className="section bg-background" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
              {t('kids.why.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground max-w-2xl mx-auto">
              {t('kids.why.title')}
            </h2>
          </AnimateOnScroll>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map(({ key, icon: Icon }) => (
              <StaggerItem key={key}>
                <div className="h-full bg-card rounded-xl border border-border p-6">
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 mb-4">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="font-semibold text-foreground mb-2">{t(`kids.why.${key}.title`)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(`kids.why.${key}.desc`)}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* --------------------------------------------------------------- Paths */}
      <section className="section bg-muted/40" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
              {t('kids.paths.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('kids.paths.title')}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">{t('kids.paths.subtitle')}</p>
          </AnimateOnScroll>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PATHS.map((p) => (
              <StaggerItem key={p}>
                <div className="h-full bg-card rounded-xl border border-border p-6 flex flex-col">
                  <span className="inline-flex self-start items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-semibold px-3 py-1 mb-4">
                    {t(`kids.paths.${p}.age`)}
                  </span>
                  <h3 className="font-semibold text-foreground mb-4">{t(`kids.paths.${p}.title`)}</h3>
                  <ul className="space-y-2.5">
                    {(['b1', 'b2', 'b3'] as const).map((b) => (
                      <li key={b} className="flex gap-2.5 text-sm text-muted-foreground">
                        <BadgeCheck
                          className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <span className="leading-relaxed">{t(`kids.paths.${p}.${b}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ------------------------------------------------------------ Teachers */}
      <section className="section bg-background" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
              {t('kids.teachers.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('kids.teachers.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('kids.teachers.subtitle')}</p>
          </AnimateOnScroll>

          {teachersLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <TeacherGridCardSkeleton key={i} />
              ))}
            </div>
          ) : teachersError ? (
            <p className="text-center text-muted-foreground">{t('kids.teachers.error')}</p>
          ) : teachers.length === 0 ? (
            <p className="text-center text-muted-foreground">{t('kids.teachers.empty')}</p>
          ) : (
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {teachers.slice(0, KIDS_TEACHER_SHOWN).map((teacher) => (
                <StaggerItem key={teacher.name}>
                  <TeacherCard teacher={teacher} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}

          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/teachers" className="flex items-center gap-2">
                {t('kids.teachers.viewAll')}
                <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- Courses */}
      {(coursesLoading || courses.length > 0) && (
        <section className="section bg-muted/40" dir={dir}>
          <div className="container">
            <AnimateOnScroll className="text-center mb-14">
              <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
                {t('kids.courses.eyebrow')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                {t('kids.courses.title')}
              </h2>
            </AnimateOnScroll>

            {coursesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <CourseGridCardSkeleton key={i} />
                ))}
              </div>
            ) : (
              <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course) => (
                  <StaggerItem key={course.name}>
                    <CourseCard course={course} />
                  </StaggerItem>
                ))}
              </StaggerContainer>
            )}

            <div className="text-center mt-10">
              <Button asChild variant="outline" size="lg" className="rounded-xl">
                <Link to="/courses" className="flex items-center gap-2">
                  {t('kids.courses.viewAll')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* --------------------------------------------------------- How it works */}
      <section className="section bg-background" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
              {t('kids.how.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('kids.how.title')}</h2>
          </AnimateOnScroll>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW.map(({ key, icon: Icon }, index) => (
              <StaggerItem key={key}>
                <div className="h-full bg-card rounded-xl border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="text-2xl font-bold text-muted-foreground/40">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t(`kids.how.${key}.title`)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(`kids.how.${key}.desc`)}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* -------------------------------------------------------- Safeguarding */}
      <section className="section bg-muted/40" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
              {t('kids.safety.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {t('kids.safety.title')}
            </h2>
          </AnimateOnScroll>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {SAFETY.map(({ key, icon: Icon }) => (
              <StaggerItem key={key}>
                <div className="h-full bg-card rounded-xl border border-border p-6 flex gap-4">
                  <span className="inline-flex items-center justify-center h-11 w-11 shrink-0 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">
                      {t(`kids.safety.${key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(`kids.safety.${key}.desc`)}
                    </p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ----------------------------------------------------------------- FAQ */}
      <section className="section bg-background" dir={dir}>
        <div className="container max-w-3xl">
          <AnimateOnScroll className="text-center mb-10">
            <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
              {t('kids.faq.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t('kids.faq.title')}</h2>
          </AnimateOnScroll>

          <Accordion type="multiple" className="space-y-2">
            {FAQ_KEYS.map((k) => (
              <AccordionItem
                key={k}
                value={k}
                className="bg-card border border-border rounded-xl px-5"
              >
                <AccordionTrigger className="text-start text-sm font-medium text-foreground hover:no-underline py-4">
                  {t(`kids.faq.q${k}`)}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4 pt-0 leading-relaxed">
                  {t(`kids.faq.a${k}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ----------------------------------------------------------------- CTA */}
      <section
        className="relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark"
        dir={dir}
      >
        <div className="absolute inset-0 islamic-pattern-overlay opacity-60" />
        <div className="container relative z-10 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('kids.cta.title')}</h2>
          <p className="text-emerald-100/80 max-w-xl mx-auto mb-8 leading-relaxed">
            {t('kids.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-7 h-12 text-base shadow-glow-gold"
            >
              <Link to="/register/parent" className="flex items-center gap-2">
                {t('kids.cta.primary')}
                <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-xl px-7 h-12 text-base"
            >
              <Link to="/teachers">{t('kids.cta.secondary')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default QuranForKids;
